"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { SurggieState } from "@/stores/game-store";
import { xpToNextLevel } from "@/stores/game-store";
import { BattleSprite } from "./BattleSprite";

// ─── Palette (PIXEL_STYLE.md — 16-color master) ───────────────────────────────
const INK = "#1a1a1a";
const CREAM = "#f5ecd6";
const SAND = "#b89968";
const WALNUT = "#5c4a30";
const GOLD = "#c5a83a";
const MOSS = "#7a9a4a";
const OCEAN = "#6a9bc4";
const ALERT_RED = "#c54a4a";

export interface SurggieDialogProps {
  surggie: SurggieState;
  onClose: () => void;
}

const STATUS_LABEL: Record<SurggieState["status"], string> = {
  idle: "IDLE",
  working: "WORKING",
  resting: "RESTING",
};

const STATUS_COLOR: Record<SurggieState["status"], string> = {
  idle: SAND,
  working: MOSS,
  resting: OCEAN,
};

function buildFlavorLines(s: SurggieState): string[] {
  return [
    `Hey boss! ${s.name} here, holding it down for team ${s.team}.`,
    `Being a ${s.role} isn't just a job — it's a calling. Also it pays in XP.`,
    s.status === "working"
      ? "Can't chat too long — these tasks won't crush themselves."
      : s.status === "resting"
        ? "Five more minutes... even Surggies need to recharge."
        : "Just enjoying the sea breeze. Need anything done?",
    `One day I'll hit Lv ${s.level + 10}. Watch me.`,
  ];
}

const PET_LINES = [
  "!!! ... okay that's the best thing that's happened all day.",
  "Hehe. Do that again and I'm filing it as a perk.",
  "Morale +10. Scientifically proven.",
];

export function SurggieDialog({ surggie, onClose }: SurggieDialogProps) {
  const flavorRef = useRef<string[]>(buildFlavorLines(surggie));
  const [line, setLine] = useState(`${surggie.name} turns to face you.`);
  const [talkIndex, setTalkIndex] = useState(0);
  const [petIndex, setPetIndex] = useState(0);
  // Incrementing key re-triggers the heart burst animation; 0 = hidden.
  const [heartBurst, setHeartBurst] = useState(0);
  const heartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTalk = useCallback(() => {
    const lines = flavorRef.current;
    setLine(lines[talkIndex % lines.length]);
    setTalkIndex((i) => i + 1);
  }, [talkIndex]);

  const handlePet = useCallback(() => {
    setLine(PET_LINES[petIndex % PET_LINES.length]);
    setPetIndex((i) => i + 1);
    setHeartBurst((k) => k + 1);
    if (heartTimerRef.current) clearTimeout(heartTimerRef.current);
    heartTimerRef.current = setTimeout(() => setHeartBurst(0), 900);
  }, [petIndex]);

  useEffect(() => {
    return () => {
      if (heartTimerRef.current) clearTimeout(heartTimerRef.current);
    };
  }, []);

  // Keyboard: 1 = Talk, 2 = Pet, 3 / Esc / E = Close.
  // Capture phase + stopPropagation so the game's window-level key handler
  // never records these presses while the dialog is open.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!["1", "2", "3", "Escape", "e", "E"].includes(e.key)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return; // act on fresh presses only
      if (e.key === "1") handleTalk();
      else if (e.key === "2") handlePet();
      else onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [handleTalk, handlePet, onClose]);

  const xpNeeded = xpToNextLevel(surggie.level);
  const xpPct = Math.max(0, Math.min(100, (surggie.xp / xpNeeded) * 100));

  const buttonStyle: React.CSSProperties = {
    background: CREAM,
    border: `2px solid ${INK}`,
    color: INK,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.05em",
    cursor: "pointer",
    boxShadow: `inset -2px -2px 0 0 ${SAND}`,
  };

  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center font-mono"
      style={{ background: "rgba(26, 26, 26, 0.55)" }}
      data-testid="surggie-dialog"
    >
      <style>{`
        @keyframes surggie-heart-rise {
          0%   { transform: translateY(0) scale(0.6); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translateY(-36px) scale(1.1); opacity: 0; }
        }
        .surggie-heart {
          position: absolute;
          font-size: 16px;
          color: ${ALERT_RED};
          animation: surggie-heart-rise 0.8s steps(6) forwards;
          pointer-events: none;
        }
        .surggie-dialog-btn:hover { background: ${SAND} !important; }
      `}</style>

      <div
        style={{
          width: 440,
          background: CREAM,
          border: `2px solid ${INK}`,
          boxShadow: `inset -2px -2px 0 0 ${SAND}, 6px 6px 0 0 rgba(26, 26, 26, 0.45)`,
          padding: 12,
          imageRendering: "pixelated",
        }}
      >
        {/* ── Header: portrait + identity ── */}
        <div className="flex gap-3">
          <div
            className="relative flex items-end justify-center shrink-0"
            style={{
              width: 96,
              height: 148,
              background: SAND,
              border: `2px solid ${INK}`,
              boxShadow: `inset -2px -2px 0 0 ${WALNUT}`,
            }}
          >
            <BattleSprite
              speciesId={surggie.spriteKey}
              orientation="front"
              scale={4}
              battlePortrait
            />
            {heartBurst > 0 && (
              <div key={heartBurst} className="absolute inset-0">
                <span className="surggie-heart" style={{ left: 16, top: 44 }}>
                  ♥
                </span>
                <span
                  className="surggie-heart"
                  style={{ left: 42, top: 30, animationDelay: "0.12s" }}
                >
                  ♥
                </span>
                <span
                  className="surggie-heart"
                  style={{ left: 66, top: 48, animationDelay: "0.24s" }}
                >
                  ♥
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0" style={{ color: INK }}>
            <div className="flex items-baseline justify-between gap-2">
              <span
                className="truncate"
                style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.05em" }}
              >
                {surggie.name.toUpperCase()}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700 }}>
                Lv {surggie.level}
              </span>
            </div>

            <div className="mt-1 flex items-center gap-2">
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: CREAM,
                  background: STATUS_COLOR[surggie.status],
                  border: `1px solid ${INK}`,
                  padding: "1px 6px",
                }}
              >
                {STATUS_LABEL[surggie.status]}
              </span>
            </div>

            <div className="mt-2" style={{ fontSize: 11 }}>
              <div>
                <span style={{ color: WALNUT }}>ROLE</span>{" "}
                {surggie.role.toUpperCase()}
              </div>
              <div>
                <span style={{ color: WALNUT }}>TEAM</span>{" "}
                {surggie.team.toUpperCase()}
              </div>
            </div>

            {/* XP bar */}
            <div className="mt-3">
              <div
                className="flex justify-between"
                style={{ fontSize: 9, color: WALNUT, fontWeight: 700 }}
              >
                <span>XP</span>
                <span>
                  {surggie.xp}/{xpNeeded}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  background: WALNUT,
                  border: `1px solid ${INK}`,
                }}
              >
                <div
                  style={{
                    width: `${xpPct}%`,
                    height: "100%",
                    background: GOLD,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Text box ── */}
        <div
          className="mt-3"
          style={{
            border: `2px solid ${INK}`,
            background: CREAM,
            boxShadow: `inset -2px -2px 0 0 ${SAND}`,
            padding: "8px 10px",
            minHeight: 52,
            fontSize: 12,
            lineHeight: 1.5,
            color: INK,
          }}
        >
          {line}
        </div>

        {/* ── Actions ── */}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="surggie-dialog-btn"
            style={buttonStyle}
            onClick={handleTalk}
          >
            [1] TALK
          </button>
          <button
            type="button"
            className="surggie-dialog-btn"
            style={buttonStyle}
            onClick={handlePet}
          >
            [2] PET
          </button>
          <button
            type="button"
            className="surggie-dialog-btn"
            style={buttonStyle}
            onClick={onClose}
          >
            [3] CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}
